import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { MarkdownRenderer } from '../components/editor/MarkdownRenderer'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

export function SharedDocumentPage() {
  const { shareId } = useParams<{ shareId: string }>()
  const navigate = useNavigate()
  const { firebaseUser } = useAuth()
  const [document, setDocument] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [requiresAuth, setRequiresAuth] = useState(false)
  const [requiresPassword, setRequiresPassword] = useState(false)
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState<string | null>(null)

  const fetchSharedDocument = async (withPassword?: string) => {
    if (!shareId) return
    try {
      setIsLoading(true)
      setError(null)
      setPasswordError(null)

      // 共有アクセスAPIを呼び出す（パスワード付きはPOSTで送る）
      const response = await fetch(`${API_BASE}/v1/shares/${shareId}`, {
        method: withPassword ? 'POST' : 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(firebaseUser ? { 'Authorization': `Bearer ${await firebaseUser.getIdToken()}` } : {})
        },
        ...(withPassword ? { body: JSON.stringify({ password: withPassword }) } : {})
      })

      if (!response.ok) {
        const errData = await response.json().catch(() => null)
        if (response.status === 401 && /password/i.test(errData?.error || '')) {
          setRequiresPassword(true)
          if (withPassword) setPasswordError('パスワードが違います')
        } else if (response.status === 401) {
          setRequiresAuth(true)
          setError('この共有リンクにアクセスするにはログインが必要です')
        } else if (response.status === 404) {
          setError('共有リンクが見つかりません')
        } else if (response.status === 403 && /expired/i.test(errData?.error || '')) {
          setError('この共有リンクは有効期限が切れています')
        } else if (response.status === 403) {
          setError('この共有リンクにアクセスする権限がありません')
        } else {
          setError('ドキュメントの読み込みに失敗しました')
        }
        return
      }

      const data = await response.json()
      setRequiresPassword(false)
      setDocument(data.document)
    } catch (err) {
      console.error('Failed to fetch shared document:', err)
      setError('ドキュメントの読み込みに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchSharedDocument()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareId, firebaseUser])

  if (isLoading) {
    return (
      <div className="flex-1 overflow-y-auto w-full flex items-center justify-center" style={{ background: 'var(--color-surface)' }}>
        <div className="flex flex-col items-center gap-3">
          <span className="spinner lg" />
          <p className="text-secondary">読み込み中...</p>
        </div>
      </div>
    )
  }

  if (requiresPassword && !document) {
    return (
      <div className="flex-1 overflow-y-auto w-full flex items-center justify-center" style={{ background: 'var(--color-surface)' }}>
        <div className="card" style={{ maxWidth: 420, width: '100%', margin: '0 var(--space-4)' }}>
          <div className="empty-state">
            <div className="empty-state-icon icon-wrap-accent">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h2 className="empty-state-title">パスワード保護</h2>
            <p className="empty-state-desc">この共有リンクはパスワードで保護されています。</p>
            <form
              onSubmit={(e) => { e.preventDefault(); if (password) fetchSharedDocument(password) }}
              className="flex flex-col gap-2"
              style={{ marginTop: 'var(--space-4)', width: '100%' }}
            >
              <input
                type="password"
                className="form-input"
                placeholder="パスワード"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
              {passwordError && <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{passwordError}</p>}
              <button type="submit" className="btn btn-primary" disabled={!password || isLoading}>
                {isLoading && <span className="spinner sm" />}
                開く
              </button>
            </form>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 overflow-y-auto w-full flex items-center justify-center" style={{ background: 'var(--color-surface)' }}>
        <div className="card" style={{ maxWidth: 420, width: '100%', margin: '0 var(--space-4)' }}>
          <div className="empty-state">
            <div className="empty-state-icon icon-wrap-danger">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 9v4M12 17h.01" />
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              </svg>
            </div>
            <h2 className="empty-state-title">アクセスエラー</h2>
            <p className="empty-state-desc">{error}</p>
            {requiresAuth ? (
              <button onClick={() => navigate('/login')} className="btn btn-primary" style={{ marginTop: 'var(--space-4)' }}>
                ログイン
              </button>
            ) : (
              <button onClick={() => navigate('/')} className="btn btn-outline" style={{ marginTop: 'var(--space-4)' }}>
                ホームに戻る
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (!document) {
    return null
  }

  return (
    <div className="flex-1 overflow-y-auto w-full" style={{ background: 'var(--color-surface)' }}>
      <div className="container-md" style={{ padding: 'var(--space-8) var(--space-6)' }}>
        {/* ヘッダー */}
        <div className="flex items-center justify-between" style={{ marginBottom: 'var(--space-6)' }}>
          <div className="flex items-center gap-3">
            <span className="icon-wrap icon-wrap-md icon-wrap-accent">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" />
              </svg>
            </span>
            <h1 className="text-xl font-semibold text-primary">共有ドキュメント</h1>
          </div>
          <span className="badge badge-neutral">読み取り専用</span>
        </div>

        {/* コンテンツ */}
        <div className="card">
          {/* タイトル */}
          <h2 className="text-2xl font-bold text-primary" style={{ marginBottom: 'var(--space-6)' }}>
            {document.title}
          </h2>

          {/* メタ情報 */}
          {document.participants && document.participants.length > 0 && (
            <div style={{ marginBottom: 'var(--space-6)', paddingBottom: 'var(--space-6)', borderBottom: '1px solid var(--color-border-light)' }}>
              <div className="text-sm text-secondary">
                <span className="font-medium">参加者:</span> {document.participants.join(', ')}
              </div>
            </div>
          )}

          {/* フォーマット済み議事録 */}
          {document.formatted?.markdown && (
            <div className="max-w-none">
              <MarkdownRenderer content={document.formatted.markdown} />
            </div>
          )}

          {/* フォーマット済みがない場合は文字起こしを表示 */}
          {!document.formatted?.markdown && document.transcript?.text && (
            <div className="max-w-none">
              <pre className="whitespace-pre-wrap text-sm text-secondary" style={{ fontFamily: 'var(--font-stack)' }}>
                {document.transcript.text}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
